/**
 * The pending deep link (twin slice 1, plan W4 step 13): the ops
 * click-through's drawer, held across the screen change.
 *
 * An ops click on a toast or bell row navigates to the alert's screen and
 * asks it to open the matching drawer with the `nx:alert-deep-link` window
 * event. The provider used to fire that event BEFORE navigating, so it
 * reached only the screen already mounted: from the Overview, a lift
 * alert opened Devices with no lift selected, because the Devices listener
 * did not exist yet when the event fired. Routes unmount on navigation.
 *
 * So the provider now also HOLDS the link, with the path it is for, and
 * delivers it ONCE, the first time the location commits to that path --
 * in an effect of the provider, which runs after the new screen's own
 * effects have registered its listener. A guard that turns the screen
 * away still commits the path for a moment, so the link is spent there
 * and opens nothing when someone visits the screen by hand later.
 *
 * `PENDING_DEEP_LINK_MS` bounds the hold. With this app's router the
 * commit follows the navigate at once, so the bound is a backstop rather
 * than the mechanism: a link whose path is not reached within 5 s is
 * dropped, never delivered late.
 *
 * What it does NOT fix: a screen whose listener matches against data it
 * has not loaded yet (Tasks matches the id against its task list) still
 * opens nothing if that list is not in the cache when it mounts. That
 * was as true of the same-screen click before; the Devices drawer, the
 * cross-screen case for lifts and doors, selects by name and opens.
 *
 * Ages are on `monotonicNow()` (a wall-clock step must not keep a link
 * alive or kill it early).
 */
import type { AlertDeepLinkKind } from './context';

/** How long a held link waits for its screen. */
export const PENDING_DEEP_LINK_MS = 5_000;

export interface PendingDeepLink {
  readonly link: AlertDeepLinkKind;
  /** The PATHNAME the link is for: no query, no hash. */
  readonly path: string;
  /** `monotonicNow()` when it was held. */
  readonly atMs: number;
}

/** `/devices?x=1#y` -> `/devices`: what a location's pathname is compared with. */
export function pathnameOf(path: string): string {
  const cut = path.search(/[?#]/);
  return cut < 0 ? path : path.slice(0, cut);
}

export function holdDeepLink(
  link: AlertDeepLinkKind,
  path: string,
  nowMs: number,
): PendingDeepLink {
  return { link, path: pathnameOf(path), atMs: nowMs };
}

/**
 * What to do with a held link at `pathname`, now:
 *   `deliver`  the location is on its path, inside the window;
 *   `wait`     not there yet, inside the window;
 *   `drop`     the window has passed (or the clock reads before the hold,
 *              which no monotonic clock does: never delivered late).
 */
export function pendingDecision(
  pending: PendingDeepLink,
  pathname: string,
  nowMs: number,
): 'deliver' | 'wait' | 'drop' {
  const age = nowMs - pending.atMs;
  if (!(age >= 0 && age <= PENDING_DEEP_LINK_MS)) return 'drop';
  return pathname === pending.path ? 'deliver' : 'wait';
}
